import { useState, useEffect, useRef, useMemo } from 'react';
import { ChatWidget } from './components/ChatWidget';
import { FlowEngine } from './lib/flowEngine';
import { sendAIMessage, inferService } from './lib/aiChatService';
import { dispatchMessages, dispatchUserMessage } from './lib/messageDispatcher';
import { getTemplateApiUrl, getSubmitLeadV2ApiUrl, getFeedbackApiUrl, API_BASE_URL } from './config/api';
import {
    trackAnalytics,
    sendGtagEvent,
    notifyParentChatOpenState,
    getTrackingSessionId,
    hasTrackedStage,
    markTrackedStage,
    resetTrackingSession
} from './lib/utils';
import { validateWidgetPhone } from './lib/phoneValidation';
import { getAttribution } from './lib/attribution';
import { uploadSmilePhoto } from './lib/smilePhotoService';
import type { Message, RegularMessage, OptionsMessage } from './types';
import type { BuilderJSON } from './types/builder';

interface AppProps {
    botId: string;
}

/** v1 widget parity: email / phone (allowed chars + 10–15 digits) / name (2–25). */
function validateCollectedField(fieldRaw: string, text: string): string | null {
    const f = fieldRaw.toLowerCase();
    const t = text.trim();
    if (f === 'email') {
        if (!/^\S+@\S+\.\S+$/.test(t)) return 'Please enter a valid email address.';
        return null;
    }
    if (f === 'phone') {
        return validateWidgetPhone(text);
    }
    if (f === 'name') {
        if (t.length < 2) return 'Please enter a valid name (at least 2 characters).';
        if (t.length > 25) return 'Please enter a valid name (maximum 25 characters).';
        return null;
    }
    return null;
}

function getSelectedOptionForText(
    engine: FlowEngine,
    messages: Message[],
    text: string
) {
    const currentState = engine.getCurrentState();
    if (!currentState || !engine.hasOptions()) return null;
    const lastBotMessage = messages
        .slice()
        .reverse()
        .find((msg) => msg.type === 'options' && msg.sender === 'bot');
    if (lastBotMessage && lastBotMessage.type === 'options') {
        return (
            lastBotMessage.options.find((opt) => opt.text === text || opt.value === text) ?? null
        );
    }
    return null;
}

function App({ botId }: AppProps) {
    const [messages, setMessages] = useState<Message[]>([]);
    const [isTyping, setIsTyping] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    // Track mobile viewport so the launcher can sit higher on phones. Kept in JS
    // (not a CSS breakpoint class) because the widget's stylesheet is served from
    // a CDN and may lag the JS bundle — an inline value always applies.
    const [isMobile, setIsMobile] = useState<boolean>(
        typeof window !== 'undefined' && typeof window.matchMedia === 'function'
            ? window.matchMedia('(max-width: 639px)').matches
            : false
    );
    const [inputEnabled, setInputEnabled] = useState(false);
    const [customStyles, setCustomStyles] = useState<any>(null);
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [undoConfirmation, setUndoConfirmation] = useState<{ targetIndex: number } | null>(null);
    const [templateLoaded, setTemplateLoaded] = useState(false); // Track if template loaded successfully
    const [showFeedback, setShowFeedback] = useState(false); // Show feedback UI after chat completes
    const [chatCompleted, setChatCompleted] = useState(false); // Track when flow has reached end
    const [isSubmittingLead, setIsSubmittingLead] = useState(false); // Track lead submission in progress
    const [showLeadCTA, setShowLeadCTA] = useState(false); // Show manual "Talk to our team" button in AI mode
    const [contactButtonsVisible, setContactButtonsVisible] = useState(false); // Contact buttons visibility
    const [aiEnabled, setAiEnabled] = useState(false); // Whether AI mode is available for this bot
    const [validationError, setValidationError] = useState<string | null>(null);
    const hasInitialized = useRef(false);
    const flowEngineRef = useRef<FlowEngine | null>(null);
    const hasTrackedOpened = useRef(false);
    const hasTrackedEngaged = useRef(false);
    const initialMessageCountRef = useRef(0);
    const hasDispatchedWelcome = useRef(false); // Track if welcome messages have been dispatched
    const dispatchSessionId = useRef(0); // Track current dispatch session for cancellation

    // Keep the mobile flag in sync with the viewport width.
    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
        const mql = window.matchMedia('(max-width: 639px)');
        const update = () => setIsMobile(mql.matches);
        update();
        mql.addEventListener('change', update);
        return () => mql.removeEventListener('change', update);
    }, []);

    // Load chatbot template and initialize
    useEffect(() => {
        if (hasInitialized.current) return;
        loadBuilderFlow();
    }, [botId]);

    // When embedded in an iframe (e.g. zentri.ai), tell the parent to resize so the
    // launcher does not block page clicks. No-op on direct embeds (other client sites).
    useEffect(() => {
        notifyParentChatOpenState(isOpen);
    }, [isOpen]);

    // Track chatbot_opened event when widget opens
    useEffect(() => {
        if (isOpen && botId && !hasTrackedOpened.current) {
            // Counted once per session. The guard is the recorded stage, not the
            // existence of a session id — the id is now minted when the flow
            // loads, before the visitor has opened anything.
            if (hasTrackedStage(botId, 'opened')) {
                hasTrackedOpened.current = true;
                return;
            }

            const trackingSessionId = getTrackingSessionId(botId);

            // Track to backend
            trackAnalytics(botId, 'chatbot_opened', trackingSessionId, API_BASE_URL).catch(err => {
                console.error('[CHATBOT-WIDGET] Failed to track chatbot_opened:', err);
            });

            // Track to Google Analytics
            sendGtagEvent('chatbot_opened_zentri', botId);

            markTrackedStage(botId, 'opened');
            hasTrackedOpened.current = true;
        }
    }, [isOpen, botId]);

    // Dispatch welcome messages when chatbot opens for the first time
    useEffect(() => {
        const initializeMessages = async () => {
            if (isOpen && templateLoaded && flowEngineRef.current && !hasDispatchedWelcome.current) {
                hasDispatchedWelcome.current = true;

                const engine = flowEngineRef.current;
                const dispatcher = { setIsTyping, setMessages };

                // Capture current session ID for cancellation
                const currentSessionId = dispatchSessionId.current;
                const checkCancelled = () => dispatchSessionId.current !== currentSessionId;

                try {
                    // Get and dispatch welcome messages with cancellation check
                    const welcomeMessages = await engine.initialize();
                    await dispatchMessages(welcomeMessages, dispatcher, {
                        ...animConfig,
                        checkCancelled
                    });

                    // Check if cancelled before auto-advance
                    if (checkCancelled()) return;

                    await autoAdvanceIfNeeded(engine, dispatcher, {
                        ...animConfig,
                        checkCancelled
                    });

                    // Store initial message count (to prevent undoing welcome messages)
                    setMessages(prev => {
                        initialMessageCountRef.current = prev.length;
                        return prev;
                    });
                } catch (error) {
                    console.error('[CHATBOT-WIDGET] Error initializing messages:', error);
                }
            }
        };

        initializeMessages();
    }, [isOpen, templateLoaded]);

    const loadBuilderFlow = async () => {
        hasInitialized.current = true;

        try {
            //     console.log('[CHATBOT-WIDGET] Fetching template for botId:', botId);

            const templateUrl = getTemplateApiUrl(botId);
            const response = await fetch(templateUrl);

            if (!response.ok) {
                throw new Error(`Failed to fetch template: ${response.status}`);
            }

            const result = await response.json();

            if (!result.success || !result.template) {
                throw new Error('Invalid template response');
            }

            const templateData = result.template as BuilderJSON;
            //     console.log('[CHATBOT-WIDGET] Template loaded:', templateData.name);
            //     console.log('[CHATBOT-WIDGET] Template flow data:', JSON.stringify(templateData.flow, null, 2));

            // Set custom styles
            if (templateData.customStyles) {
                setCustomStyles(templateData.customStyles);

                // Handle auto-open if enabled
                if (templateData.customStyles.autoOpen) {
                    const delay = templateData.customStyles.autoOpenDelay || 2000;
                    //     console.log(`[CHATBOT-WIDGET] Auto-open enabled with ${delay}ms delay`);
                    setTimeout(() => {
                        setIsOpen(true);
                    }, delay);
                }
            }

            // The conversation and the analytics funnel share one id. Minting a
            // separate one here is what used to make lead_submitted impossible to
            // join back to the open and the engagement it followed.
            const sessionId = getTrackingSessionId(botId);
            //     console.log('[CHATBOT-WIDGET] Initializing with sessionId:', sessionId);
            setSessionId(sessionId);

            // Initialize flow engine with sessionId and AI enabled status
            const aiEnabled = result.ai_enabled === true;
            setAiEnabled(aiEnabled);
            const engine = new FlowEngine(templateData.flow, sessionId, aiEnabled);
            flowEngineRef.current = engine;

            // Mark template as successfully loaded
            // Messages will be dispatched when chatbot opens
            setTemplateLoaded(true);

        } catch (error) {
            console.error('[CHATBOT-WIDGET] Error loading template:', error);
            console.log('[CHATBOT-WIDGET] Chatbot button will not be displayed due to template loading failure');
            setTemplateLoaded(false);
        }
    };

    const autoAdvanceIfNeeded = async (
        engine: FlowEngine,
        dispatcher: { setIsTyping: (typing: boolean) => void; setMessages: (updater: (prev: Message[]) => Message[]) => void },
        animConfig?: { typingDuration?: number; messageDelay?: number; checkCancelled?: () => boolean }
    ) => {
        if (animConfig?.checkCancelled && animConfig.checkCancelled()) {
            return;
        }

        while (engine.shouldAutoAdvance()) {
            // Check if cancelled before auto-advancing
            if (animConfig?.checkCancelled && animConfig.checkCancelled()) {
                return;
            }

            const result = await engine.autoAdvance();

            if (result.messages.length > 0) {
                await dispatchMessages(result.messages, dispatcher, animConfig || {});
            }

            // Check if cancelled after dispatching
            if (animConfig?.checkCancelled && animConfig.checkCancelled()) {
                return;
            }

            // If we just entered AI mode, stop auto-advancing and enable input
            if (engine.isInAIConversation()) {
                setInputEnabled(true);
                // Show "Talk to our team" immediately in AI mode, hide contact buttons
                setShowLeadCTA(true);
                setContactButtonsVisible(false);
                break;
            }

            if (result.hasReachedEnd) {
                if (animConfig?.checkCancelled && animConfig.checkCancelled()) {
                    return;
                }

                const sessionData = engine.getSessionData();

                // Submit to backend with loading indicator
                setIsSubmittingLead(true);
                await submitChatCompletion(sessionData);
                if (animConfig?.checkCancelled && animConfig.checkCancelled()) {
                    return;
                }
                setIsSubmittingLead(false);
                setChatCompleted(true);
                setInputEnabled(false);

                break;
            }

            setInputEnabled(engine.shouldEnableInput());

            if (!engine.shouldAutoAdvance()) {
                break;
            }
        }

        setInputEnabled(engine.shouldEnableInput());

        // Show contact buttons when past welcome state and not in AI mode
        const currentState = engine.getCurrentState();
        if (currentState && currentState.id !== 'welcome' && !engine.isInAIConversation()) {
            setContactButtonsVisible(true);
        }
    };

    const handlePhotoSubmit = async (file: File): Promise<string | null> => {
        const engine = flowEngineRef.current;
        if (!engine || !engine.isAwaitingPhoto()) return null;
        const stateId = engine.getCurrentState()!.id;
        const currentSessionId = dispatchSessionId.current;

        try {
            const uploaded = await uploadSmilePhoto(botId, file);
            // Restart replaced this chat while the photo was uploading.
            if (dispatchSessionId.current !== currentSessionId || flowEngineRef.current !== engine) {
                return null;
            }
            engine.addAttachment({ stateId, ...uploaded });
        } catch (error) {
            return error instanceof Error ? error.message : 'Could not upload your photo. Please try again.';
        }

        if (dispatchSessionId.current !== currentSessionId || flowEngineRef.current !== engine) {
            return null;
        }

        handleSendMessage('Photo uploaded');
        return null;
    };

    const handleSendMessage = (text: string): void | false => {
        const engine = flowEngineRef.current;
        if (!engine) return;

        if (!text?.trim()) return;

        if (!engine.isInAIConversation()) {
            const currentState = engine.getCurrentState();
            const field = currentState?.field_to_collect;
            const selectedOption = getSelectedOptionForText(engine, messages, text);
            if (field && !selectedOption) {
                const err = validateCollectedField(String(field), text);
                if (err) {
                    setValidationError(err);
                    return false;
                }
            }
        }
        setValidationError(null);

        const dispatcher = { setIsTyping, setMessages };
        const currentSessionId = dispatchSessionId.current;
        // Restart bumps dispatchSessionId and swaps the engine. An in-flight
        // reply must not write into the chat that replaced it.
        const checkCancelled = () =>
            dispatchSessionId.current !== currentSessionId || flowEngineRef.current !== engine;
        const sessionAnim = { ...animConfig, checkCancelled };

        void (async () => {
        if (checkCancelled()) return;

        // Track user_engaged event on first user message
        if (!hasTrackedEngaged.current) {
            if (hasTrackedStage(botId, 'engaged')) {
                if (!checkCancelled()) hasTrackedEngaged.current = true;
            } else {
                try {
                    const trackingSessionId = getTrackingSessionId(botId);
                    //     console.log('[CHATBOT-WIDGET] Tracking user_engaged event', { trackingSessionId });

                    // Track to backend
                    await trackAnalytics(botId, 'user_engaged', trackingSessionId, API_BASE_URL);

                    if (checkCancelled()) return;

                    // Track to Google Analytics
                    sendGtagEvent('user_engaged_zentri', botId);

                    hasTrackedEngaged.current = true;
                    markTrackedStage(botId, 'engaged');
                } catch (error) {
                    console.error('[CHATBOT-WIDGET] Failed to track user_engaged:', error);
                }
            }
        }

        if (checkCancelled()) return;

        dispatchUserMessage(text, dispatcher);
        setInputEnabled(false);

        // ─── AI Conversation Mode ────────────────────────────────────
        if (engine.isInAIConversation()) {
            const aiConfig = engine.getAIConfig();
            if (!aiConfig) {
                console.error('[CHATBOT-WIDGET] AI mode active but no aiConfig found');
                setInputEnabled(true);
                return;
            }

            setIsTyping(true);

            try {
                const aiResponse = await sendAIMessage({
                    bot_id: botId,
                    message: text,
                    conversation_history: engine.getAIConversationHistory(),
                    context: {
                        business_name: aiConfig.business_name,
                        turn_count: engine.getAITurnCount(),
                        custom_instructions: aiConfig.custom_instructions,
                        lead_capture_turn_threshold: aiConfig.lead_capture_turn_threshold,
                    },
                });

                setIsTyping(false);
                if (checkCancelled()) return;

                // Record the exchange in the engine
                engine.addAIExchange(text, aiResponse.message);

                // "Talk to our team" stays visible throughout AI mode (set on AI entry)

                // Dispatch AI response — if options are present, combine message text into the options message
                const hasOptions = aiResponse.ui && aiResponse.ui.type === 'options' && aiResponse.ui.options.length > 0;

                if (hasOptions && aiResponse.action !== 'request_lead_capture') {
                    const optionsMessage: OptionsMessage = {
                        type: 'options',
                        text: aiResponse.message,
                        options: aiResponse.ui!.options.map(opt => ({
                            text: opt,
                            value: opt,
                        })),
                        showPerRow: 2,
                        displayStyle: 'cards',
                        sender: 'bot',
                        timestamp: new Date(),
                        flow_state: engine.getCurrentState()?.id || '',
                    };
                    await dispatchMessages([optionsMessage], dispatcher, sessionAnim);
                } else if (aiResponse.action !== 'request_lead_capture') {
                    const aiMessage: RegularMessage = {
                        type: 'regular',
                        text: aiResponse.message,
                        sender: 'bot',
                        timestamp: new Date(),
                        flow_state: engine.getCurrentState()?.id || '',
                    };
                    await dispatchMessages([aiMessage], dispatcher, sessionAnim);
                }

                if (checkCancelled()) return;

                // Check if AI wants to hand off to lead capture
                if (aiResponse.action === 'request_lead_capture') {
                    setShowLeadCTA(false);
                    setContactButtonsVisible(true); // Restore contact buttons on AI exit
                    const nextStateId = engine.exitAIMode();
                    if (nextStateId) {
                        // Transition to the lead capture state
                        const nextMessages = engine.transitionTo(nextStateId);
                        if (nextMessages.length > 0) {
                            await dispatchMessages(nextMessages, dispatcher, sessionAnim);
                        }

                        if (checkCancelled()) return;

                        // Continue auto-advancing if needed
                        await autoAdvanceIfNeeded(engine, dispatcher, sessionAnim);

                        // Check for end state / lead submission
                        if (checkCancelled()) return;
                        if (engine.hasReachedEnd()) {
                            const sessionData = engine.getSessionData();
                            setIsSubmittingLead(true);
                            await submitChatCompletion(sessionData);
                            if (checkCancelled()) return;
                            setIsSubmittingLead(false);
                            setChatCompleted(true);
                            setInputEnabled(false);
                            return;
                        }
                    } else {
                        // No next state — treat as end of flow
                        const sessionData = engine.getSessionData();
                        setIsSubmittingLead(true);
                        await submitChatCompletion(sessionData);
                        if (checkCancelled()) return;
                        setIsSubmittingLead(false);
                        setChatCompleted(true);
                        setInputEnabled(false);
                        return;
                    }
                }

                if (checkCancelled()) return;

                // Keep input enabled in AI mode (user can always type)
                setInputEnabled(engine.shouldEnableInput());

            } catch (error) {
                console.error('[CHATBOT-WIDGET] AI chat error:', error);
                setIsTyping(false);
                if (checkCancelled()) return;

                // Dispatch fallback error message
                const errorMessage: RegularMessage = {
                    type: 'regular',
                    text: "I'm having trouble connecting right now. Please try again.",
                    sender: 'bot',
                    timestamp: new Date(),
                    flow_state: engine.getCurrentState()?.id || '',
                };
                await dispatchMessages([errorMessage], dispatcher, sessionAnim);
                if (checkCancelled()) return;
                setInputEnabled(true);
            }

            return;
        }

        // ─── Standard Flow Mode ──────────────────────────────────────
        const currentState = engine.getCurrentState();
        let selectedOption = null;

        if (currentState && engine.hasOptions()) {
            const lastBotMessage = messages.slice().reverse().find(msg =>
                msg.type === 'options' && msg.sender === 'bot'
            );

            if (lastBotMessage && lastBotMessage.type === 'options') {
                selectedOption = lastBotMessage.options.find(opt =>
                    opt.text === text || opt.value === text
                );
            }
        }

        try {
            if (checkCancelled()) return;

            const result = await engine.handleUserInput(text, selectedOption);

            if (checkCancelled()) return;

            if (result.messages.length > 0) {
                await dispatchMessages(result.messages, dispatcher, sessionAnim);
            }

            if (checkCancelled()) return;

            // Check if we just entered AI mode (transition into an AI state)
            if (engine.isInAIConversation()) {
                setInputEnabled(true);
                // Show "Talk to our team" immediately, hide contact buttons
                setShowLeadCTA(true);
                setContactButtonsVisible(false);
                return;
            }

            if (result.hasReachedEnd) {
                const sessionData = engine.getSessionData();
                //     console.log('=== CHAT COMPLETED ===');
                //     console.log('Session:', sessionData.sessionId);
                //     console.log('Data:', sessionData.collectedFields);

                // Submit to backend with loading indicator
                setIsSubmittingLead(true);
                await submitChatCompletion(sessionData);
                if (checkCancelled()) return;
                setIsSubmittingLead(false);
                setChatCompleted(true);

                setInputEnabled(false);
                return;
            }

            await autoAdvanceIfNeeded(engine, dispatcher, sessionAnim);

        } catch (error) {
            console.error('Error processing input:', error);
            if (!checkCancelled()) setInputEnabled(true);
        }
        })();
    };

    /**
     * Manual lead capture — triggered by the "Talk to our team" CTA button.
     * Mirrors the AI-driven request_lead_capture flow.
     */
    // const handleManualLeadCapture = async () => {
    //     const engine = flowEngineRef.current;
    //     if (!engine || !engine.isInAIConversation()) return;

    //     const dispatcher = { setIsTyping, setMessages };
    //     setShowLeadCTA(false);
    //     setInputEnabled(false);
    //     // Restore contact buttons now that "Talk to our team" is dismissed
    //     setContactButtonsVisible(true);

    //     const nextStateId = engine.exitAIMode();
    //     if (nextStateId) {
    //         const nextMessages = engine.transitionTo(nextStateId);
    //         if (nextMessages.length > 0) {
    //             await dispatchMessages(nextMessages, dispatcher, animConfig);
    //         }

    //         await autoAdvanceIfNeeded(engine, dispatcher, animConfig);

    //         if (engine.hasReachedEnd()) {
    //             const sessionData = engine.getSessionData();
    //             setIsSubmittingLead(true);
    //             await submitChatCompletion(sessionData);
    //             setIsSubmittingLead(false);
    //             setChatCompleted(true);
    //             setInputEnabled(false);
    //             return;
    //         }
    //     } else {
    //         // No next state — treat as end of flow
    //         const sessionData = engine.getSessionData();
    //         setIsSubmittingLead(true);
    //         await submitChatCompletion(sessionData);
    //         setIsSubmittingLead(false);
    //         setChatCompleted(true);
    //         setInputEnabled(false);
    //         return;
    //     }

    //     setInputEnabled(engine.shouldEnableInput());
    // };

    const submitChatCompletion = async (sessionData: any) => {
        try {
            //     console.log('[CHATBOT-WIDGET] Submitting chat completion to backend...');

            // Infer service from AI conversation if not directly captured
            const collectedFields = { ...sessionData.collectedFields };
            if (!collectedFields.service && !collectedFields.service_interest) {
                const aiHistory = flowEngineRef.current?.getAIConversationHistory();

                if (aiHistory && aiHistory.length > 0) {
                    const inferredService = await inferService(botId, aiHistory);
                    if (inferredService) {
                        collectedFields.service = inferredService;
                    }
                }
            }

            const submitUrl = getSubmitLeadV2ApiUrl(botId);
            const response = await fetch(submitUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    sessionData: {
                        sessionId: sessionData.sessionId,
                        chatHistory: sessionData.chatHistory,
                        collectedFields: collectedFields,
                        currentState: sessionData.currentState,
                        timestamp: sessionData.timestamp
                    },
                    // Raw origin signals captured on page load. The backend classifies
                    // them and honours the client's attribution setting.
                    attribution: getAttribution(),
                    attachments: (flowEngineRef.current?.getAttachments() || [])
                        .map(({ file, ext, token }) => ({ file, ext, token }))
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(`Submission failed: ${errorData.message || response.statusText}`);
            }

            const result = await response.json();
            //     console.log('[CHATBOT-WIDGET] Submission successful:', result);

            // Track lead_submitted event. Deliberately the tracking session id
            // rather than the conversation's own, so this submission sits in the
            // same funnel as the open and the engagement that led to it.
            trackAnalytics(botId, 'lead_submitted', getTrackingSessionId(botId), API_BASE_URL).catch(err => {
                console.error('[CHATBOT-WIDGET] Failed to track lead_submitted:', err);
            });

            // Track to Google Analytics
            sendGtagEvent('lead_submitted_zentri', botId);

            // After a successful lead submission, clear the widget session tracking
            // so that the next conversation uses a fresh sessionId (avoids duplicate-key errors).
            resetTrackingSession(botId);

            return result;
        } catch (error) {
            console.error('[CHATBOT-WIDGET] Error submitting chat completion:', error);
            // Don't throw - allow chat to complete even if submission fails
        }
    };

    // [v2] Handle feedback submission after chat completion
    const handleFeedbackSubmit = async (rating: number, comment?: string) => {
        const effectiveSessionId = flowEngineRef.current?.getSessionData()?.sessionId || sessionId;
        const feedbackUrl = getFeedbackApiUrl();
        const response = await fetch(feedbackUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                botId,
                sessionId: effectiveSessionId,
                rating,
                comment: comment ? '[v2] ' + comment : undefined,
            }),
        });
        if (!response.ok) {
            const errData = await response.json().catch(() => ({}));
            throw new Error(errData.error || `HTTP ${response.status}`);
        }
    };

    // [v2] End conversation: clear chat and show feedback
    const handleEndConversation = () => {
        setMessages([]);
        setInputEnabled(false);
        setShowFeedback(true);
        setChatCompleted(false);
        setContactButtonsVisible(false);
        setValidationError(null);
    };

    const handleRestart = async () => {
        // Increment session ID to cancel all ongoing dispatches
        dispatchSessionId.current += 1;

        // Clear everything first
        setMessages([]);
        setInputEnabled(false);
        setIsTyping(false);
        setUndoConfirmation(null);
        setShowFeedback(false);
        setChatCompleted(false);
        setIsSubmittingLead(false);
        setShowLeadCTA(false);
        setContactButtonsVisible(false);
        setValidationError(null);

        // Reset the engine
        flowEngineRef.current = null;

        // Reset ALL flags to clean state
        hasInitialized.current = false;
        hasDispatchedWelcome.current = false;
        hasTrackedOpened.current = false;
        hasTrackedEngaged.current = false;
        initialMessageCountRef.current = 0;

        // Always start a fresh widget session when reloading the chatbot,
        // so subsequent leads use a new sessionId.
        resetTrackingSession(botId);

        // Temporarily set templateLoaded to false to prevent effect from firing prematurely
        setTemplateLoaded(false);

        // Wait a small tick to ensure state updates are processed
        await new Promise(resolve => setTimeout(resolve, 50));

        // Reload the flow - this will set templateLoaded back to true when complete
        await loadBuilderFlow();

        // The useEffect will now dispatch welcome messages automatically
        // since isOpen=true, templateLoaded=true, and hasDispatchedWelcome=false
    };

    // Undo handlers
    const handleUndoRequest = (targetIndex: number) => {
        //     console.log('[CHATBOT-WIDGET] Undo requested for index:', targetIndex);
        setUndoConfirmation({ targetIndex });
    };

    const handleUndoCancel = () => {
        //     console.log('[CHATBOT-WIDGET] Undo cancelled');
        setUndoConfirmation(null);
    };

    const handleUndoConfirm = async () => {
        if (!undoConfirmation) return;
        const engine = flowEngineRef.current;
        if (!engine) return;

        const targetIndex = undoConfirmation.targetIndex;
        //     console.log('[CHATBOT-WIDGET] Undo confirmed for index:', targetIndex);

        // Cannot undo if typing or no messages
        if (isTyping || messages.length === 0) {
            // console.log('[CHATBOT-WIDGET] Undo blocked - typing or no messages');
            setUndoConfirmation(null);
            return;
        }

        // Cannot undo initial welcome messages
        if (targetIndex < initialMessageCountRef.current) {
            // console.log('[CHATBOT-WIDGET] Undo blocked - cannot undo welcome messages');
            setUndoConfirmation(null);
            return;
        }

        // console.log('[CHATBOT-WIDGET] Executing undo:', {
        //     targetIndex,
        //     currentMessagesCount: messages.length,
        //     initialMessageCount: initialMessageCountRef.current
        // });

        // Truncate messages to the point before the target message
        const truncatedMessages = messages.slice(0, targetIndex);
        setMessages(truncatedMessages);

        // Restore FlowEngine state
        engine.restoreToPoint(truncatedMessages);

        // Clear states
        setUndoConfirmation(null);
        setIsTyping(false);
        setValidationError(null);

        // Determine if input should be enabled based on restored state
        setInputEnabled(engine.shouldEnableInput());

        // Update contact buttons / lead CTA based on restored state
        if (engine.isInAIConversation()) {
            setShowLeadCTA(true);
            setContactButtonsVisible(false);
        } else {
            setShowLeadCTA(false);
            const currentState = engine.getCurrentState();
            setContactButtonsVisible(currentState ? currentState.id !== 'welcome' : false);
        }

        // Auto-advance if needed (in case the restored state should auto-advance)
        const dispatcher = { setIsTyping, setMessages };
        const currentSessionId = dispatchSessionId.current;
        await autoAdvanceIfNeeded(engine, dispatcher, {
            ...animConfig,
            checkCancelled: () =>
                dispatchSessionId.current !== currentSessionId || flowEngineRef.current !== engine,
        });

        // console.log('[CHATBOT-WIDGET] Undo complete:', {
        //     newMessagesCount: truncatedMessages.length,
        //     currentState: engine.getCurrentState()?.id,
        //     inputEnabled: engine.shouldEnableInput()
        // });
    };

    const canUndoToMessage = (index: number): boolean => {
        const engine = flowEngineRef.current;

        // Cannot undo during typing
        if (isTyping) return false;

        // Cannot undo if chat has reached end
        if (engine && engine.hasReachedEnd()) return false;

        // Cannot undo initial welcome messages
        if (index < initialMessageCountRef.current) return false;

        // Must be a user message
        if (messages[index]?.sender !== 'user') return false;

        return true;
    };

    // Default styles if not loaded from template
    const styles = customStyles || {
        primaryColor: '#667eea',
        secondaryColor: '#764ba2',
        botAvatar: 'https://api.dicebear.com/7.x/bottts/svg?seed=chatbot',
        userAvatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=user',
        borderRadius: 16,
        backgroundStyle: 'solid' as const,
        backgroundColor: '#ffffff',
    };

    // Generate background style based on backgroundStyle setting
    const backgroundStyle = useMemo(() => {
        const bgStyle = styles.backgroundStyle || 'solid';
        const bgColor = styles.backgroundColor || '#ffffff';
        const shadowIntensity = styles.shadowIntensity || 0.2;
        const shadowColor = styles.shadowColor || '#000000';
        const borderRadius = styles.borderRadius || 16;

        // Convert hex color to RGB for rgba shadow
        const r = parseInt(shadowColor.slice(1, 3), 16);
        const g = parseInt(shadowColor.slice(3, 5), 16);
        const b = parseInt(shadowColor.slice(5, 7), 16);
        const boxShadow = `0 4px 20px rgba(${r}, ${g}, ${b}, ${shadowIntensity})`;

        if (bgStyle === 'transparent') {
            return {
                backgroundColor: 'rgba(255, 255, 255, 0)',
                backdropFilter: 'none',
                WebkitBackdropFilter: 'none',
                boxShadow: 'none',
                borderRadius: `${borderRadius}px`
            };
        } else if (bgStyle === 'glass') {
            return {
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(20px) saturate(180%)',
                WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                borderRadius: `${borderRadius}px`
            };
        } else {
            return {
                backgroundColor: bgColor,
                boxShadow,
                borderRadius: `${borderRadius}px`
            };
        }
    }, [styles.backgroundStyle, styles.backgroundColor, styles.shadowIntensity, styles.shadowColor, styles.borderRadius]);

    const animConfig = useMemo(() => ({
        typingDuration: styles.typingDelay || 1200,
        messageDelay: styles.messageAppearDelay || 800,
    }), [styles.typingDelay, styles.messageAppearDelay]);

    const phoneFieldActive = useMemo(() => {
        const e = flowEngineRef.current;
        if (!e || e.isInAIConversation()) return false;
        return String(e.getCurrentState()?.field_to_collect || '').toLowerCase() === 'phone';
    }, [messages, inputEnabled, chatCompleted, isTyping]);

    return (
        <>
            {/* Chatbot Widget - Fixed Position Bottom Right */}
            {isOpen && (
                <div className="zentri-fixed zentri-bottom-0 zentri-right-0 zentri-z-[9999999999] zentri-p-3 sm:zentri-p-4">
                    <div
                        className="zentri-h-[80vh] zentri-w-[calc(100vw-24px)] sm:zentri-w-[440px] zentri-rounded-2xl zentri-shadow-2xl zentri-overflow-hidden zentri-transition-all zentri-duration-300 zentri-ease-out"
                        style={backgroundStyle}
                    >
                        <ChatWidget
                            messages={messages}
                            botId={botId}
                            sessionId={sessionId || undefined}
                            apiBaseUrl={API_BASE_URL}
                            customStyles={styles}
                            onSendMessage={handleSendMessage}
                            onPhotoSubmit={handlePhotoSubmit}
                            onClose={() => setIsOpen(false)}
                            onRestart={handleRestart}
                            isTyping={isTyping}
                            inputEnabled={inputEnabled}
                            isSubmittingLead={isSubmittingLead}
                            leadCTAVisible={showLeadCTA}
                            undoConfirmation={undoConfirmation}
                            onUndoRequest={handleUndoRequest}
                            onUndoConfirm={handleUndoConfirm}
                            onUndoCancel={handleUndoCancel}
                            canUndoToMessage={canUndoToMessage}
                            showFeedback={showFeedback}
                            onFeedbackSubmit={handleFeedbackSubmit}
                            onEndConversation={handleEndConversation}
                            chatCompleted={chatCompleted}
                            contactButtons={customStyles?.contactButtons}
                            contactButtonsVisible={contactButtonsVisible}
                            aiEnabled={aiEnabled}
                            validationError={validationError}
                            onClearValidationError={() => setValidationError(null)}
                            textInputMode={phoneFieldActive ? 'numeric' : 'text'}
                        />
                    </div>
                </div>
            )}

            {/* Toggle Button (when closed) - Only show if template loaded successfully */}
            {!isOpen && templateLoaded && (
                <div className="zentri-fixed zentri-right-0 zentri-flex zentri-flex-col zentri-items-end" style={{ bottom: isMobile ? '48px' : '24px', zIndex: 2147483646 }}>
                    <div className="zentri-relative zentri-mb-3 zentri-mr-3">
                        <div
                            role="button"
                            tabIndex={0}
                            onClick={() => setIsOpen(true)}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsOpen(true); } }}
                            className="zentri-relative zentri-overflow-hidden zentri-shadow-xl zentri-border zentri-border-black/5 hover:zentri-shadow-2xl zentri-transition-all zentri-cursor-pointer"
                            style={{ minWidth: 220, padding: '4px', borderRadius: 9999 }}
                            aria-label="Open chat"
                        >
                            <span
                                style={{
                                    position: 'absolute',
                                    inset: '-1000%',
                                    animation: 'spin 4s linear infinite',
                                    background: 'conic-gradient(from 90deg at 50% 50%, #60A5FA 0%, #A855F7 50%, #EC4899 100%, #60A5FA 100%)'
                                }}
                            />
                            <span className="zentri-relative zentri-flex zentri-items-center zentri-gap-3 zentri-px-4 zentri-py-2 zentri-text-left" style={{ backgroundColor: '#ffffff', borderRadius: 9999 }}>
                                <span
                                    className="zentri-relative zentri-flex-shrink-0 zentri-w-12 zentri-h-12 zentri-rounded-full zentri-flex zentri-items-center zentri-justify-center zentri-bg-white"
                                    style={{ boxShadow: `0 0 0 1px ${styles.primaryColor}` }}
                                >
                                    <span className="zentri-block zentri-w-11 zentri-h-11 zentri-rounded-full zentri-overflow-hidden">
                                        <img
                                            src={styles.botAvatar}
                                            alt="Assistant"
                                            className="zentri-w-full zentri-h-full zentri-object-cover"
                                        />
                                    </span>
                                    <span className="zentri-absolute zentri--top-1 zentri--right-1 zentri-w-5 zentri-h-5 zentri-rounded-full zentri-bg-red-500 zentri-text-white zentri-text-[10px] zentri-font-bold zentri-flex zentri-items-center zentri-justify-center zentri-shadow zentri-z-10">1</span>
                                </span>
                                <span className="zentri-text-[15px] zentri-font-semibold zentri-leading-5 zentri-text-gray-800">How can we help?</span>
                            </span>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default App;

